import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { NEVER, catchError, filter, map, of, switchMap, take, tap } from 'rxjs';

import { PostHogService } from '@core/analytics/posthog.service';
import { TenantService } from '@core/api/tenant/tenant.service';
import { ThemePreference } from '@core/api/user-preferences/user-preferences.models';
import { UserPreferencesService } from '@core/api/user-preferences/user-preferences.service';
import { UsersService } from '@core/api/users/users.service';
import { getImpersonationTokenClaims } from '@core/helpers/impersonation-token-claims';
import { clearPersistedImpersonation, persistImpersonation } from '@core/helpers/impersonation-token-persistence';
import { isKioskUrl } from '@core/helpers/is-kiosk-url';
import { clearKioskPersistedTokens } from '@core/helpers/kiosk-token-persistence';
import { prefersDark$ } from '@core/helpers/prefers-color-scheme';
import { timestampExpired$ } from '@core/helpers/timestamp-expired';
import { clearPersistedTokens } from '@core/helpers/token-persistence';
import { ThemeService } from '@core/services/theme.service';
import { ToastService } from '@core/services/toast.service';
import { resetAiAssistant } from '@core/stores/ai-assistant/ai-assistant.actions';
import { ChargerNamesStore } from '@core/stores/charger-names/charger-names.store';
import { CustomOcppCommandStore } from '@core/stores/custom-ocpp-command/custom-ocpp-command.store';
import { SiteStructureStore } from '@core/stores/site-structure/site-structure.store';
import { TenantsStore } from '@core/stores/tenants/tenants.store';
import { VehicleNamesStore } from '@core/stores/vehicle-names/vehicle-names.store';
import { connectToWebsocket } from '@core/stores/websocket/websocket.actions';

import { clearConversationTitles } from '@domain/ai-assistant/conversation-titles';
import { mapUiThemeApiModelToStoreModel } from '@domain/theme/theme-models.mappings';
import { ThemeStoreModel } from '@domain/theme/theme.models';

import { ToastMessageType } from '@shared/components/toast-message/toast-message.view-model';
import { runOutsideAngularZone } from '@shared/operators/run-outside-angular-zone.operator';
import { AssetDetailsDialogStore } from '@shared/stores/asset-details-dialog/asset-details-dialog.store';
import { isDefined } from '@shared/utils/is-defined';
import { StoreToStorageSync } from '@shared/utils/store-to-storage-sync';

import {
  activeUserChanged,
  exitImpersonation,
  impersonationFailure,
  impersonationSuccess,
  loadTenantSettings,
  loadTenantSettingsFailure,
  loadTenantSettingsSuccess,
  loadTenantTheme,
  loadTenantThemeFailure,
  loadTenantThemeSuccess,
  loadUserPreferences,
  loadUserPreferencesFailure,
  loadUserPreferencesSuccess,
  logout,
  renewImpersonation,
  saveUserPreferences,
  saveUserPreferencesFailure,
  saveUserPreferencesSuccess,
  setInitialTokenAndPermissions,
  setSystemPrefersDark,
  setThemePreference,
  startImpersonation,
} from './auth.actions';
import { selectImpersonationTargetUserId, selectIsDarkTheme, selectRefreshTokenExpiresTimestamp, selectTheme } from './auth.selectors';

@Injectable()
export class AuthEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #keycloakService = inject(KeycloakService);
  readonly #posthogService = inject(PostHogService);
  readonly #themeService = inject(ThemeService);
  readonly #tenantService = inject(TenantService);
  readonly #tenantsStore = inject(TenantsStore);
  readonly #userPreferencesService = inject(UserPreferencesService);
  readonly #toastService = inject(ToastService);
  readonly #translateService = inject(TranslateService);
  readonly #ngZone = inject(NgZone);
  readonly #siteStructureStore = inject(SiteStructureStore);
  readonly #chargerNamesStore = inject(ChargerNamesStore);
  readonly #vehicleNamesStore = inject(VehicleNamesStore);
  readonly #customOcppCommandStore = inject(CustomOcppCommandStore);
  readonly #assetDetailsDialogStore = inject(AssetDetailsDialogStore);
  readonly #usersService = inject(UsersService);
  readonly #router = inject(Router);

  public startImpersonation$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(startImpersonation),
      switchMap(({ targetUserId }) => this.#requestImpersonation$(targetUserId, true))
    )
  );

  public renewImpersonation$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(renewImpersonation),
      switchMap(() =>
        this.#store.select(selectImpersonationTargetUserId).pipe(
          take(1),
          switchMap((targetUserId) =>
            isDefined(targetUserId)
              ? this.#requestImpersonation$(targetUserId)
              : of(impersonationFailure({ error: 'No impersonation target user is available' }))
          )
        )
      )
    )
  );

  public persistImpersonation$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(impersonationSuccess),
        tap(({ token, targetUserId }) => persistImpersonation(token, targetUserId))
      ),
    { dispatch: false }
  );

  public navigateAfterStartingImpersonation$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(impersonationSuccess),
        filter(({ navigateHome }) => !!navigateHome),
        tap(() => void this.#router.navigate(['/']))
      ),
    { dispatch: false }
  );

  public exitImpersonation$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(exitImpersonation),
        tap(() => {
          clearPersistedImpersonation();
          void this.#router.navigate(['/']);
        })
      ),
    { dispatch: false }
  );

  public resetIdentityBoundStateAfterImpersonation$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(impersonationSuccess, exitImpersonation),
      filter((action) => action.type === exitImpersonation.type || ('navigateHome' in action && action.navigateHome === true)),
      tap(() => {
        this.#assetDetailsDialogStore.close();
        this.#tenantsStore.clearTenants();
        this.#tenantsStore.ensureTenantsLoaded();
        this.#siteStructureStore.clear();
        this.#chargerNamesStore.clearChargerNames();
        this.#vehicleNamesStore.clearVehicleNames();
        this.#customOcppCommandStore.reset();
        this.#toastService.dismissToast();
        StoreToStorageSync.clearStorageData();
        clearConversationTitles();

        this.#store.dispatch(resetAiAssistant());
        this.#store.dispatch(loadTenantTheme());
        this.#store.dispatch(loadTenantSettings());
        this.#store.dispatch(loadUserPreferences());
        this.#store.dispatch(connectToWebsocket());
      }),
      map(() => activeUserChanged())
    )
  );

  public loadTenantThemeAfterLogin$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(setInitialTokenAndPermissions),
      map(({ token }) => token),
      filter((token): token is string => !!token),
      map(() => loadTenantTheme())
    )
  );

  public loadTenantSettingsAfterLogin$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(setInitialTokenAndPermissions),
      map(({ token }) => token),
      filter((token): token is string => !!token),
      map(() => loadTenantSettings())
    )
  );

  public ensureTenantsLoadedAfterLogin$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setInitialTokenAndPermissions),
        map(({ token }) => token),
        filter((token): token is string => !!token),
        tap(() => this.#tenantsStore.ensureTenantsLoaded())
      ),
    { dispatch: false }
  );

  public loadUserPreferencesAfterLogin$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(setInitialTokenAndPermissions),
      map(({ token }) => token),
      filter((token): token is string => !!token && !isKioskUrl()),
      map(() => loadUserPreferences())
    )
  );

  public setLightThemeForKiosk$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(setInitialTokenAndPermissions),
      map(({ token }) => token),
      filter((token): token is string => !!token && isKioskUrl()),
      map(() => setThemePreference({ themePreference: ThemePreference.Light }))
    )
  );

  public loadTenantSettings$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadTenantSettings),
      switchMap(() =>
        this.#tenantService.getTenantSettings$().pipe(
          map((response) => loadTenantSettingsSuccess({ response })),
          catchError(() =>
            of(
              loadTenantSettingsFailure({
                error: 'Failed to load tenant settings',
              })
            )
          )
        )
      )
    )
  );

  public loadTenantTheme$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadTenantTheme),
      switchMap(() =>
        this.#tenantService.getTenantTheme$().pipe(
          map(({ light, dark }) =>
            loadTenantThemeSuccess({
              lightTheme: mapUiThemeApiModelToStoreModel(light),
              darkTheme: mapUiThemeApiModelToStoreModel(dark),
            })
          ),
          catchError(() =>
            of(
              loadTenantThemeFailure({
                error: 'Failed to load tenant theme',
              })
            )
          )
        )
      )
    )
  );

  public loadUserPreferences$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadUserPreferences),
      switchMap(() =>
        this.#userPreferencesService.getUserPreferences$().pipe(
          map((preferences) => loadUserPreferencesSuccess({ preferences })),
          catchError(() =>
            of(
              loadUserPreferencesFailure({
                error: 'Failed to load user preferences',
              })
            )
          )
        )
      )
    )
  );

  public saveUserPreferences$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(saveUserPreferences),
      switchMap(({ preferences }) =>
        this.#userPreferencesService.saveUserPreferences$(preferences).pipe(
          map((savedPreferences) => saveUserPreferencesSuccess({ preferences: savedPreferences })),
          catchError(() =>
            of(
              saveUserPreferencesFailure({
                error: 'Failed to save user preferences',
              })
            )
          )
        )
      )
    )
  );

  public showSaveSuccessToast$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(saveUserPreferencesSuccess),
        tap(() =>
          this.#toastService.openToast({
            type: ToastMessageType.Success,
            message$: this.#translateService.stream('preferences.save-success-toast'),
            closeable: true,
            duration: 5000,
          })
        )
      ),
    { dispatch: false }
  );

  public setCssVariables$ = createEffect(
    () =>
      this.#store.select(selectTheme).pipe(
        filter((theme): theme is ThemeStoreModel => !!theme),
        tap((theme) => this.#themeService.setCssVariables(theme))
      ),
    {
      dispatch: false,
    }
  );

  /* public toggleThemeEvery15SecondsForTesting$ = createEffect(() =>
    timer(0, 10000).pipe(
      switchMap(() =>
        this.#store.select(selectIsDarkTheme).pipe(
          take(1),
          map((isDark) => setSystemPrefersDark({ systemPrefersDark: !isDark }))
        )
      )
    )
  ); */

  public setRootClass$ = createEffect(
    () =>
      this.#store.select(selectIsDarkTheme).pipe(
        filter((isDark) => isDark !== undefined),
        tap((isDark) => this.#themeService.setRootClass(!!isDark))
      ),
    {
      dispatch: false,
    }
  );

  public watchSystemPrefersDark$ = createEffect(() =>
    prefersDark$.pipe(map((systemPrefersDark) => setSystemPrefersDark({ systemPrefersDark })))
  );

  public autoLogoutOnRefreshTokenExpiration$ = createEffect(() =>
    this.#store.select(selectRefreshTokenExpiresTimestamp).pipe(
      switchMap((refreshTokenExpiresTimestamp) => {
        if (!refreshTokenExpiresTimestamp || isKioskUrl()) {
          return NEVER;
        }

        return timestampExpired$(refreshTokenExpiresTimestamp).pipe(
          runOutsideAngularZone(this.#ngZone, true),
          map(() => logout())
        );
      })
    )
  );

  public logout$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(logout),
        tap(() => {
          this.#posthogService.posthog.reset();
          this.#tenantsStore.clearTenants();
          if (isKioskUrl()) {
            clearKioskPersistedTokens();
          } else {
            clearPersistedTokens();
            clearPersistedImpersonation();
          }
          this.#keycloakService.logout();
        })
      ),
    {
      dispatch: false,
    }
  );

  #requestImpersonation$(targetUserId: string, navigateHome = false) {
    return this.#usersService.impersonate$(targetUserId).pipe(
      map((response) => {
        const claims = getImpersonationTokenClaims(response.accessToken);
        if (!claims) {
          throw new Error('Invalid impersonation access token');
        }

        return impersonationSuccess({ token: response.accessToken, targetUserId, ...claims, navigateHome });
      }),
      catchError(() => of(impersonationFailure({ error: 'Failed to impersonate user' })))
    );
  }
}
